import { useState, type FormEvent } from 'react';
import { LogIn, Loader2 } from 'lucide-react';
import { ApiError } from '../lib/api-client.js';
import { useAuth } from '../auth.js';

/**
 * The web sign-in gate. Shown whenever there is no live session; on success the
 * AuthProvider flips to `signedIn` and the app shell takes over.
 */
export function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'INVALID_CREDENTIALS') {
        setError('That email and password don’t match.');
      } else if (err instanceof ApiError && err.code === 'NETWORK_ERROR') {
        setError('Could not reach the server. Is the API running?');
      } else {
        setError(err instanceof ApiError ? err.message : 'Sign-in failed.');
      }
      setBusy(false);
    }
  }

  return (
    <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
      <form className="zone" onSubmit={onSubmit} style={{ width: 360, maxWidth: '90vw', padding: 28 }}>
        <div className="wordmark" style={{ fontSize: 26, marginBottom: 2 }}>
          Kolec<b>t</b>ra
        </div>
        <div className="mini" style={{ marginBottom: 20 }}>web · sign in to continue</div>

        {error && (
          <div className="banner bad" style={{ marginBottom: 14 }}>
            {error}
          </div>
        )}

        <div className="field">
          <label>Email</label>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@project.dev"
            autoComplete="username"
          />
        </div>

        <div className="field">
          <label>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
          />
        </div>

        <button
          className="btn pri"
          type="submit"
          disabled={busy || !email || !password}
          style={{ width: '100%', justifyContent: 'center', marginTop: 6 }}
        >
          {busy ? <Loader2 size={15} strokeWidth={2} className="spin" /> : <LogIn size={15} strokeWidth={1.8} />}
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
